import {
  Component,
  Input,
  ViewChild,
  AfterViewInit,
  Output,
  EventEmitter,
  NgZone,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';

@Component({
  selector: 'app-google-map',
  imports: [GoogleMapsModule],
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss'],
})
export class GoogleMapComponent implements AfterViewInit, OnChanges {
  @ViewChild('mapRef', { static: false }) mapRef!: GoogleMap;

  @Input() height: string = '400px';
  @Input() width: string = '100%';
  @Input() center: google.maps.LatLngLiteral = { lat: 40, lng: -100 };
  @Input() zoom: number = 5;
  @Input() options: google.maps.MapOptions = {
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    zoomControl: true,
    streetViewControl: false,
    fullscreenControl: true,
  };

  // Polygons input: decoupled overlay data
  @Input() polygons: google.maps.PolygonOptions[] = [];
  // Emits when a polygon is clicked; payload can include source options
  @Output() polygonClick = new EventEmitter<{
    event: google.maps.MapMouseEvent;
    polygon: google.maps.Polygon;
    options: google.maps.PolygonOptions;
  }>();
  @Output() idle = new EventEmitter<google.maps.LatLngBoundsLiteral>();

  private polygonInstances: google.maps.Polygon[] = [];

  constructor(private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    // Emit bounds on idle
    const gm = this.mapRef.googleMap!;
    this.zone.runOutsideAngular(() => {
      gm.addListener('idle', () => {
        const b = gm.getBounds();
        if (!b) return;
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        const literal: google.maps.LatLngBoundsLiteral = {
          north: ne.lat(),
          east: ne.lng(),
          south: sw.lat(),
          west: sw.lng(),
        };
        this.zone.run(() => this.idle.emit(literal));
      });
    });
    this.renderPolygons();
  }

  // Re-render when input changes (simple approach)
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['polygons']) {
      this.renderPolygons();
    }
  }

  private renderPolygons(): void {
    if (!this.mapRef?.googleMap) return;
    const map = this.mapRef.googleMap;
    this.polygonInstances.forEach((p) => p.setMap(null));
    this.polygonInstances = [];

    // Add new
    for (const opts of this.polygons) {
      if (
        !opts.paths ||
        (Array.isArray(opts.paths) && opts.paths.length === 0)
      ) {
        console.warn('Skipping polygon with invalid/empty paths');
        continue;
      }

      const poly = new google.maps.Polygon({
        paths: opts.paths,
        strokeColor: opts.strokeColor ?? '#1e88e5',
        strokeOpacity: opts.strokeOpacity ?? 0.8,
        strokeWeight: opts.strokeWeight ?? 2,
        fillColor: opts.fillColor ?? '#90caf9',
        fillOpacity: opts.fillOpacity ?? 0.5,
        clickable: opts.clickable ?? false,
        geodesic: opts.geodesic ?? false,
        map: map, // Set map directly in constructor
        zIndex: 1000,
      });

      if (opts.clickable !== false) {
        poly.addListener('click', (e: google.maps.MapMouseEvent) => {
          this.zone.run(() => {
            this.polygonClick.emit({ event: e, polygon: poly, options: opts });
          });
        });
      }

      poly.addListener('mouseover', () => {
        const weight = opts.strokeWeight ?? 2;
        poly.setOptions({ strokeWeight: weight + 1 });
      });

      poly.addListener('mouseout', () => {
        poly.setOptions({ strokeWeight: opts.strokeWeight ?? 2 });
      });

      this.polygonInstances.push(poly);
    }
  }
}
